import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";

import { Doughnut } from "react-chartjs-2";
import { useMemo } from "react";

ChartJS.register(ArcElement, Tooltip, Legend);

export const JobSummaryChart = ({ jobs = [] }) => {
  // Dynamically calculate chart data using useMemo so it only recalculates when 'jobs' changes
  const { totalOpen, labels, dataVals, colors } = useMemo(() => {
    // 1. Filter only open jobs
    const openJobs = jobs.filter((job) => job.status === "OPEN");

    // 2. Count jobs by their Type (Full Time, Part Time, etc.)
    const typeCounts = {};
    openJobs.forEach((job) => {
      // Clean up the string (e.g., "FULL_TIME" -> "FULL TIME")
      const type = job.jobType?.replace("_", " ") || "OTHER";
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    // 3. Sort by count and grab the top 3 for the chart
    const sortedTypes = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    // Default colors for the segments
    const palette = ["#3B5BDB", "#1E2A78", "#B8C0FF"];

    return {
      totalOpen: openJobs.length,
      labels: sortedTypes.map((t) => t[0]),
      dataVals: sortedTypes.map((t) => t[1]),
      colors: palette,
    };
  }, [jobs]);

  const data = {
    labels: labels.length > 0 ? labels : ["No Open Roles"],
    datasets: [
      {
        data: dataVals.length > 0 ? dataVals : [1], // Fallback if empty
        backgroundColor: dataVals.length > 0 ? colors : ["#F1F3F5"],
        borderWidth: 0,
        borderRadius: 20,
        spacing: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "80%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: dataVals.length > 0 },
    },
  };

  return (
    <div style={styles.wrapper}>
      <h3 style={styles.title}>Open Positions</h3>

      <div style={styles.chartContainer}>
        <Doughnut data={data} options={options} />

        <div style={styles.centerTextContainer}>
          <h2 style={styles.centerNumber}>{totalOpen}</h2>
          <p style={styles.centerLabel}>Total Open</p>
        </div>
      </div>

      <div style={styles.legendContainer}>
        {labels.map((label, index) => (
          <div key={label} style={styles.legendItem}>
            <span style={{ ...styles.dot, backgroundColor: colors[index] }} />
            <strong>{dataVals[index]}</strong>
            <span style={styles.legendText}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  wrapper: { width: "100%", fontFamily: "sans-serif" },
  title: { margin: "0 0 20px 0", fontSize: "18px", color: "#333" },
  chartContainer: { position: "relative", height: "200px", width: "100%", overflow: "hidden" },
  centerTextContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
  },
  centerNumber: { margin: 0, fontSize: "32px", color: "#111" },
  centerLabel: { margin: 0, fontSize: "12px", color: "#888" },
  legendContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  legendItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontSize: "12px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    marginBottom: "4px",
  },
  legendText: { color: "#888", marginTop: "2px", fontSize: "10px" },
};
